import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { MatchDetailItemFragment, MatchEventFragment } from '@amf/shared/types/graphql';
import classNames from 'classnames';
import { isSystemMatchEvent, MatchEventType } from '@amf/shared/data/Match';
import { useTranslate } from '@tolgee/react';
import useMatch from '@amf/shared/components/match/hooks/useMatch';

const EVENT_TIMEOUT = 8000;

type Props = {
  event: MatchEventFragment;
  match: MatchDetailItemFragment;
};

function MatchJumbotronEvent({ event, match }: Props) {
  const { mutate } = useMatch(match.id, match.live);
  const [id, setId] = useState('');
  const [visible, setVisible] = useState(false);
  const { t } = useTranslate();

  useEffect(() => {
    setVisible(true);
    setId(event.id);
    setTimeout(() => {
      if (id === event.id) setVisible(false);
    }, EVENT_TIMEOUT);
  }, [event.id, id]);

  useEffect(() => {
    mutate();
  }, [mutate, event]);

  const homeTeamId = match.home.team.id;
  const team = homeTeamId === event.teamId ? match.home.team : match.visiting.team;
  const goalTitle = event.penaltyMethod
    ? event.successGoal
      ? 'Proměněná penalta'
      : 'Neproměněná penalta'
    : 'Gól';

  if (!visible) return null;
  return (
    <motion.div
      className={classNames('MatchJumbotron__event', {
        'MatchJumbotron__event--goal':
          event.type === MatchEventType.goal &&
          (event.successGoal === true || event.successGoal === undefined),
        'MatchJumbotron__event--penalty': event.type === MatchEventType.penalty,
        'MatchJumbotron__event--red':
          event.type === MatchEventType.red ||
          (event.type === MatchEventType.yellow && event.redAfterYellowCard) ||
          (event.type === MatchEventType.goal && event.successGoal === false),
        'MatchJumbotron__event--yellow':
          event.type === MatchEventType.yellow && !event.redAfterYellowCard,
        'MatchJumbotron__event--system': isSystemMatchEvent(event),
      })}
      animate={{
        y: [-80, 0, 0, -80],
        opacity: [0.7, 1, 1, 0.7],
      }}
      transition={{
        duration: EVENT_TIMEOUT / 1000,
        stiffness: 100,
        times: [0, 0.05, 0.95, 1],
      }}
    >
      <div className='MatchJumbotron__eventIcon'>
        {event.type === MatchEventType.goal && <i className='icons-soccer-goal' />}
        {event.type === MatchEventType.yellow && <i className='bi bi-file-fill' />}
        {event.type === MatchEventType.red && <i className='bi bi-file-fill' />}
        {event.type === MatchEventType.penalty && <i className='bi bi-exclamation-diamond-fill' />}
        {isSystemMatchEvent(event) && <i className='bi bi-info-circle-fill' />}
      </div>
      <div className='MatchJumbotron__eventTitle'>
        {isSystemMatchEvent(event)
          ? t(event.title)
          : event.type === MatchEventType.goal
          ? goalTitle
          : event.title}
      </div>
      {!isSystemMatchEvent(event) && (
        <div className='MatchJumbotron__eventContent'>
          {event.message && (
            <div className='MatchJumbotron__eventSubtitle'>
              <strong>{event.message}</strong>
            </div>
          )}
          {event.player && (
            <div className='MatchJumbotron__eventSubtitle'>
              {event.type === MatchEventType.goal &&
                (event.successGoal === true || event.successGoal === undefined) &&
                t('match.event.goal.player.title')}
              {event.type === MatchEventType.goal &&
                event.successGoal === false &&
                t('match.event.goal.player.title.penalty')}
              {event.type === MatchEventType.yellow && t('match.event.yellow.player.title')}
              {event.type === MatchEventType.red && t('match.event.red.player.title')}
              {event.type === MatchEventType.penalty && t('match.event.penalty.player.title')}:{' '}
              <strong>
                {event.player.fullName}{' '}
                {event.player.currentTeam && <>({team?.name ?? event.player.currentTeam.name})</>}
              </strong>
            </div>
          )}
          {!event.player && event.type === MatchEventType.goal && (
            <div className='MatchJumbotron__eventSubtitle'>
              {(event.successGoal === true || event.successGoal === undefined) &&
                t('match.event.goal.player.title')}
              {event.successGoal === false && t('match.event.goal.player.title.penalty')}:{' '}
              <strong>{event.teamName}</strong>
            </div>
          )}
          {event.assistant && (
            <div className='MatchJumbotron__eventSubtitle'>
              {t('match.event.assist.title')}:{' '}
              <strong>
                {event.assistant.fullName}{' '}
                {event.assistant.currentTeam && (
                  <>({team?.name ?? event.assistant.currentTeam.name})</>
                )}
              </strong>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default MatchJumbotronEvent;
